import Joi from "joi";
import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getUserPageBlocks } from "../../../../../action/common/commonActionCreator";
import { createTrainingHelp } from "../../../../../action/masters/trainingHelpActionCreator";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import { UserTypes } from "../../../../../models/dashboard/adminDashboard";
import {
  ICreateTrainingHelp,
  ITrainingHelpCreateData,
} from "../../../../../models/master/trainingHelp";
import LoaderSpinner from "../../../../../common-components/loader";
import { masterType } from "../../../../../constants/masterTypes";
import { setPageBlocks } from "../../../../../store/slices/common/commonSlice";
import { maxImageSize } from "../../../../../constants/commonConstants";

const TrainingHelpCreate = (props: ICreateTrainingHelp) => {
  const {
    createTrainingHelp,
    setShowCreate,
    getAllTrainingHelps,
    getUserPageBlocks,
    pageBlocks,
    userDetails,
  } = props;
  const navigate = useNavigate();

  const initialData: ITrainingHelpCreateData = {
    name: "",
    file: null,
    isActive: true,
    priority: 0,
    pageBlockIds: [],
    collegeId: 0,
    departmentId: 0,
    videoLink: "",
  };

  // states

  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [imageName, setImageName] = useState<string | null>(null);
  const dispatch = useDispatch();

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    if (pageBlocks && pageBlocks.length > 0) {
      data.pageBlockIds = [pageBlocks[0].id];
    }
    setData({ ...data, [event.target.name]: event.target.value });
    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value), masterType.trainingHelp);
    }
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  const urlRegex =
    /^https?:\/\/([a-z0-9]{2,}(-[a-z0-9]{2,})*\.)+[a-z0-9]{2,6}(\/[a-z0-9#?=&%\/\-_\.]*)?$/i;
  const schema = Joi.object().keys({
    name: Joi.string().required().label("Name is required"),
    file: Joi.object().required().label("File is required"),
    videoLink: Joi.string()
      .required()
      .regex(urlRegex)
      .label("Enter Valid Video Link"),
  });

  const onSave = async () => {
    const validationErrors = validateFields(data, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("file", data.file);
      formData.append("videoLink", data.videoLink);
      formData.append("isActive", data.isActive.toString());
      formData.append("priority", data.priority.toString());
      formData.append(
        "departmentId",
        userDetails.currentDepartment.id.toString()
      );
      formData.append("collegeId", userDetails.currentCollege.id.toString());
      data.pageBlockIds.map((item, index) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });
      setLoading(true);
      setIsDisabled(true);
      try {
        await createTrainingHelp(formData, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  // setting sweet alert dynamically
  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllTrainingHelps();
      setShowCreate(false);
    }
  };

  //setting warning alert on cancel
  const onCancel = async () => {
    setShowAlertCancel(true);
    setAlert(alertConstants.cancelCheck);
  };

  useEffect(() => {
    getUserPageBlocks(UserTypes.Staff, masterType.trainingHelp);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setPageBlocks([]));
    };
  }, []);

  return (
    <>
      <div className="modal-header">
        <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
          Add New Training Help
        </h1>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={() => setShowCreate(false)}
        ></button>
      </div>
      <div className="modal-body">
        <div className="row">
          <div className="col-md-12 form-group">
            <FloatingLabelTextInput
              label={"Name"}
              onChange={onChange}
              isMandatory={true}
              value={data.name}
              name={"name"}
              maxLength={50}
            />
            {errors?.name && <div className="text-danger">{errors.name}</div>}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">
              Image<i className="label-asterisk"> * </i>
            </label>
            <div className="inputfile-flex">
              {imagePreview && (
                <div className="inputfile-img">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: "50px", height: "50px" }}
                  />
                </div>
              )}
              <div className="inputfile-wrap">
                <input
                  type="file"
                  name="file"
                  id="image"
                  className="form-control d-none"
                  required
                  onChange={onFileChange}
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon, text/html"
                  value={errors?.file && ""}
                />
                <div className="inputfile">
                  <label htmlFor="image">Choose File</label>
                  <span>
                    {imageName
                      ? imageName.trim().split("/").pop()
                      : "No File Choosen"}
                  </span>
                </div>
              </div>
            </div>
            {errors?.file && <div className="text-danger">{errors.file}</div>}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Video Link"}
              onChange={onChange}
              isMandatory={true}
              value={data.videoLink}
              name={"videoLink"}
              maxLength={500}
            />
            {errors?.videoLink && (
              <div className="text-danger">{errors.videoLink}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Department Name "}
              onChange={onChange}
              isMandatory={true}
              value={userDetails?.currentDepartment?.name}
              name={"departmentId"}
              disable
            />
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"College Name "}
              onChange={onChange}
              isMandatory={true}
              value={userDetails?.currentCollege?.name}
              name={"collegeId"}
              disable
            />
          </div>
          <div className="col-md-12 form-group">
            <FloatingLabelTextInput
              label={"Page-Block "}
              onChange={onChange}
              isMandatory={false}
              value={pageBlocks?.[0]?.name}
              name={"pageBlockIds"}
              disable
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={isDisabled}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            data-bs-dismiss="modal"
            onClick={() => setShowCreate(false)}
            disabled={isDisabled}
          >
            Close
          </button>
        </div>
      </div>
      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {loading && <LoaderSpinner />}
    </>
  );
};

const mapDispatchToProps = {
  createTrainingHelp: (data: any, callBack: Function) =>
    createTrainingHelp(data, callBack),
  getUserPageBlocks: (userType: number, masterType: string) =>
    getUserPageBlocks(userType, masterType),
};

function mapStateToProps(state: any) {
  return {
    pageBlocks: state.common.pageBlocks,
    userDetails: state.userDetails.userProfileData,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TrainingHelpCreate);
